import { useQuery } from '@tanstack/react-query';

import { ApiError, apiFetch } from './client';
import type { MarketplaceResponse, StackRecovery } from '@/types/api';

/** A stack pay link (`/app/shop?stack_recovery=<slug>`): what the patient
 *  still owes to complete a stack whose product declined. Read fresh every
 *  time the link is opened (it may have been paid or expired since). */
export function useStackRecovery(slug: string | null) {
  return useQuery({
    queryKey: ['me', 'stack-recovery', slug],
    queryFn: () =>
      apiFetch<StackRecovery>(`/me/stack-recoveries/${encodeURIComponent(slug!)}`),
    enabled: Boolean(slug),
    staleTime: 0,
    gcTime: 0,
    // Only a server error or a dropped connection is worth retrying.
    retry: (count, error) =>
      !(error instanceof ApiError && error.status < 500) && count < 2,
  });
}

/** The Shop listing (the same cache as `useMarketplace`), read by the cart to
 *  check the stacks it holds against what the Shop sells now. Always stale
 *  for this reader, so each time the check is enabled it fetches once. */
export function useStackListing(enabled: boolean) {
  return useQuery({
    queryKey: ['marketplace'],
    queryFn: () => apiFetch<MarketplaceResponse>('/marketplace'),
    enabled,
    staleTime: 0,
    // A focus refetch would re-lock Confirm mid-review; the cart checks again
    // the next time it opens.
    refetchOnWindowFocus: false,
  });
}
